import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { bookingsApi } from '../api/endpoints';
import BookingDrawer from '../components/BookingDrawer';
import { toast } from 'sonner';

const LOAN_BADGE = {
  'Not Applied': 'bg-slate-100 text-slate-600',
  'Logged In': 'bg-blue-50 text-blue-700 border-blue-200',
  'Query Raised': 'bg-amber-50 text-amber-700 border-amber-200',
  'Sanctioned': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Disbursed': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Rejected': 'bg-rose-50 text-rose-700 border-rose-200',
};

const Finance = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [activeBookingId, setActiveBookingId] = useState(null);
  
  // Router Driven Filter State
  const [loanStatusFilter, setLoanStatusFilter] = useState('all');

  const fetchAll = async () => {
    setLoading(true);
    try { setData(await bookingsApi.list()); }
    catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  
  useEffect(() => { fetchAll(); }, []);

  // Intercept routing payload for dashboard wiring
  useEffect(() => {
    if (location.state?.filterStatus) {
      setLoanStatusFilter(location.state.filterStatus);
    } else {
      setLoanStatusFilter('all');
    }
  }, [location.state]);

  // Compute filtered list dynamically
  const filtered = useMemo(() => {
    let result = data;

    if (loanStatusFilter !== 'all') {
      result = result.filter(b => (b.loan_status || 'Not Applied') === loanStatusFilter);
    }

    if (q) {
      const query = q.toLowerCase();
      result = result.filter(b => 
        (b.customer_name + (b.unit_no || '') + (b.project_name || '') + (b.bank_name || '')).toLowerCase().includes(query)
      );
    }

    return result;
  }, [data, q, loanStatusFilter]);

  const clearFilters = () => {
    setLoanStatusFilter('all');
    setQ('');
    navigate('.', { replace: true, state: null });
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl bg-gradient-to-r from-[#0F1F38] via-[#1B3A5C] to-[#2A4F7A] text-white p-5 lg:p-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Icons.Banknote className="w-5 h-5 text-brand-gold" />
            <div className="text-xs uppercase tracking-[0.25em] text-brand-gold">Bank Loans & Documents</div>
          </div>
          <h2 className="font-display text-2xl lg:text-3xl font-bold">Finance & Documentation</h2>
          <p className="text-slate-300 text-sm mt-1 max-w-xl">Track bank details, loan status timeline, and store every loan document per booking.</p>
        </div>
        
        {loanStatusFilter !== 'all' && (
          <Button variant="ghost" onClick={clearFilters} className="text-blue-200 hover:text-white hover:bg-white/10 uppercase tracking-wider text-[10px] font-bold">
            Clear Filter
          </Button>
        )}
      </div>

      {/* Filter Banner */}
      {loanStatusFilter !== 'all' && (
        <div className="p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg flex items-center gap-2 text-xs font-medium animate-in fade-in duration-300">
          <Icons.Filter className="w-4 h-4 text-blue-600" />
          <span>Showing bookings with loan status: <strong>{loanStatusFilter}</strong></span>
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4">
          <div className="relative">
            <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search by customer, unit, project, bank..." value={q} onChange={e => setQ(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Customer / Booking</th>
                <th className="px-4 py-3 hidden md:table-cell">Project / Unit</th>
                <th className="px-4 py-3 hidden lg:table-cell">Bank</th>
                <th className="px-4 py-3">Loan Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}><td colSpan={5} className="p-2"><Skeleton className="h-10 w-full" /></td></tr>
              ))}
              {!loading && filtered.map(b => (
                <tr key={b.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setActiveBookingId(b.id)}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{b.customer_name}</div>
                    <div className="text-[11px] text-slate-500">{b.id}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="text-slate-800">{b.project_name}</div>
                    <div className="text-[11px] text-slate-500">Unit {b.unit_no}</div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="text-slate-800 text-xs">{b.bank_name || '—'}</div>
                    <div className="text-[11px] text-slate-500">{b.bank_branch}</div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline" className={LOAN_BADGE[b.loan_status] || ''}>{b.loan_status || '—'}</Badge></td>
                  <td className="px-4 py-3 text-right text-xs text-brand-navy font-medium">View Details →</td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-slate-400">No matching records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <BookingDrawer bookingId={activeBookingId} open={!!activeBookingId} onOpenChange={(v) => !v && setActiveBookingId(null)} onChanged={fetchAll} />
    </div>
  );
};

export default Finance;